import { z } from 'zod';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { VCycleElementContainerFilterConditionSelectorSchema } from '../../../enumerations/VCycleElementContainerFilterConditionSelector';
import { VCycleElementContainerFilterLogicalConnectorSchema } from '../../../enumerations/VCycleElementContainerFilterLogicalConnector';
import { post } from '../../../resource-base/APIVersion1';
import { parseRawData } from '../../../resource-base/adapters/AdapterUtils';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import { IdSchema } from '../../../resource-base/baseSchemas';
import { msObject } from '../../../resource-base/msObject';
import { ParentedPersistableResourceSchema } from '../../../resource-base/persistableResourceTypes';
import { useMutationWithDelay } from '../../../resource-base/useMutationWithDelay';

export const VCycleElementContainerFilterStartingConditionResourceSchema = msObject({
    selector: VCycleElementContainerFilterConditionSelectorSchema,
    values: IdSchema.array(),
});

export const VCycleElementContainerFilterConnectedConditionResourceSchema = msObject({
    logicalConnector: VCycleElementContainerFilterLogicalConnectorSchema,
    selector: VCycleElementContainerFilterConditionSelectorSchema,
    values: IdSchema.array(),
});

export const VCycleElementContainerFilterResourceSchema = ParentedPersistableResourceSchema.extend({
    startingCondition: VCycleElementContainerFilterStartingConditionResourceSchema,
    otherConditions: VCycleElementContainerFilterConnectedConditionResourceSchema.array(),
    resourceType: z
        .literal(ResourceType.V_CYCLE_ELEMENT_CONTAINER_FILTER)
        .default(ResourceType.V_CYCLE_ELEMENT_CONTAINER_FILTER),
});

export const VCycleElementContainerFilterMutationResourceSchema = getMutationSchema(
    VCycleElementContainerFilterResourceSchema
);

export type VCycleElementContainerFilterResource = z.infer<typeof VCycleElementContainerFilterResourceSchema>;
type VCycleElementContainerFilterMutationResource = z.infer<typeof VCycleElementContainerFilterMutationResourceSchema>;

export const VCycleElementContainerFilterResultSchema = msObject({
    id: IdSchema,
    processAreaContainerResults: msObject({
        id: IdSchema,
        matchingVCycleElements: IdSchema.array(),
    }).array(),
    vCycleContainerResults: msObject({
        id: IdSchema,
        vCycleResult: msObject({
            id: IdSchema,
            processAreaContainerResults: msObject({
                id: IdSchema,
                matchingVCycleElements: IdSchema.array(),
            }).array(),
            vCycleContainerResults: msObject({
                id: IdSchema,
                vCycleResult: msObject({
                    id: IdSchema,
                    processAreaContainerResults: msObject({
                        id: IdSchema,
                        matchingVCycleElements: IdSchema.array(),
                    }).array(),
                    vCycleContainerResults: msObject({
                        id: IdSchema,
                    }).array(),
                }).nullable(),
            }).array(),
        }).nullable(),
    }).array(),
});

export const GridElementFilterResultSchema = msObject({
    id: IdSchema,
    matchingGridElements: IdSchema.array(),
});

export type VCycleElementContainerFilterResult = z.infer<typeof VCycleElementContainerFilterResultSchema>;
export type GridElementFilterResult = z.infer<typeof GridElementFilterResultSchema>;

export function useVCycleElementContainerFilterActions() {
    const actions = useMultiResourceSingleParentedResourceAdapterActions(
        'element-filter',
        ResourceType.V_CYCLE_ELEMENT_CONTAINER_FILTER,
        VCycleElementContainerFilterResourceSchema
    );

    const useApplyVCycleFilter = () => {
        return useMutationWithDelay(
            ({
                filterResource,
                id,
            }: {
                id: string | undefined;
                filterResource: VCycleElementContainerFilterMutationResource;
            }) =>
                post(`element-filter/filter-v-cycle-representation/${id}`, JSON.stringify(filterResource)).then(
                    (result) => {
                        return result
                            .json()
                            .then((rawData) => parseRawData(rawData, VCycleElementContainerFilterResultSchema));
                    }
                ),
            msObject({
                id: IdSchema,
                filterResource: VCycleElementContainerFilterMutationResourceSchema,
            })
        );
    };

    const useApplyGridFilter = () => {
        return useMutationWithDelay(
            ({
                filterResource,
                id,
            }: {
                id: string | undefined;
                filterResource: VCycleElementContainerFilterMutationResource;
            }) =>
                post(`element-filter/filter-grid-representation/${id}`, JSON.stringify(filterResource)).then(
                    (result) => {
                        return result.json().then((rawData) => parseRawData(rawData, GridElementFilterResultSchema));
                    }
                ),
            msObject({
                id: IdSchema,
                filterResource: VCycleElementContainerFilterMutationResourceSchema,
            })
        );
    };

    return {
        ...actions,
        useApplyVCycleFilter,
        useApplyGridFilter,
    };
}
